<template>
  <v-card class="mb-0 game-notes" elevation="1">
    <v-card-title class="d-flex align-center">
      <v-icon class="mr-2">mdi-note-text</v-icon>
      <span class="font-weight-bold">Notes</span>
      <v-spacer />
      <v-btn
        color="success"
        :disabled="!game.id"
        data-testid="game-notes-add"
        @click="showAddNoteDialog = true"
      >
        Add
      </v-btn>
    </v-card-title>

    <v-card-text class="game-notes__search py-1">
      <v-text-field
        v-model="search"
        label="Search"
        dense
        hide-details
        class="mt-0"
        prepend-icon="mdi-magnify"
      />
    </v-card-text>

    <v-data-table
      class="game-notes__table"
      :headers="headers"
      :items="notes"
      :search="search"
      :loading="isLoading"
      item-key="id"
      sort-by="createdAt"
      :sort-desc="true"
      :items-per-page="10"
      :footer-props="{ itemsPerPageOptions: [10, 25, 50, -1] }"
    >
      <template v-slot:item.note="{ item }">
        <span
          class="text-truncate d-inline-block game-notes__preview"
          :title="getNoteTitle(item)"
        >
          {{ getNoteTitle(item) }}
        </span>
      </template>
      <template v-slot:item.userName="{ item }">
        <span
          class="text-truncate d-inline-block game-notes__author"
          :class="{ 'text--secondary': !item.userName }"
          :title="item.userName || undefined"
        >{{ item.userName || '—' }}</span>
      </template>
      <template v-slot:item.createdAt="{ item }">
        <span class="game-notes__date">{{ formatDate(item.createdAt) }}</span>
      </template>
      <template v-slot:item.updatedAt="{ item }">
        <span
          v-if="item.updatedAt && item.updatedAt !== item.createdAt"
          class="game-notes__date"
        >
          {{ formatDate(item.updatedAt) }}
        </span>
        <span v-else class="text--secondary">—</span>
      </template>
      <template v-slot:item.actions="{ item }">
        <div class="game-notes__actions">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                color="primary"
                v-bind="attrs"
                v-on="on"
                :data-testid="`game-notes-edit-${item.id}`"
                @click="editNote(item)"
              >
                mdi-pencil
              </v-icon>
            </template>
            <span>Edit</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                color="secondary"
                tag="a"
                :href="noteChangeLogHref(item)"
                target="_blank"
                rel="noopener noreferrer"
                :data-testid="`game-notes-history-${item.id}`"
                v-bind="attrs"
                v-on="on"
              >
                mdi-history
              </v-icon>
            </template>
            <span>View change history</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                color="warning"
                v-bind="attrs"
                v-on="on"
                :data-testid="`game-notes-delete-${item.id}`"
                @click="deleteNote(item)"
              >
                mdi-delete
              </v-icon>
            </template>
            <span>Delete</span>
          </v-tooltip>
        </div>
      </template>
      <template v-slot:no-data>
        <div class="text-center pa-6">
          <v-icon size="64" color="grey lighten-1">mdi-note-text-outline</v-icon>
          <p class="mt-2 mb-0 grey--text">No notes yet. Add your first note to get started.</p>
        </div>
      </template>
    </v-data-table>

    <v-dialog v-model="showAddNoteDialog" max-width="640" persistent>
      <v-card class="admin-dialog">
        <v-card-title class="d-flex align-center admin-dialog__title">
          <span class="font-weight-bold">{{ editingNote ? 'Edit Note' : 'Add New Note' }}</span>
          <v-spacer />
          <v-btn icon small @click="cancelEdit">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="admin-dialog__body">
          <v-form ref="noteForm" v-model="noteFormValid">
            <v-textarea
              v-model="noteForm.note"
              label="Note *"
              :rules="[rules.required]"
              rows="6"
              required
              dense
              outlined
              hint="You can use basic HTML formatting"
              persistent-hint
            />
          </v-form>
        </v-card-text>
        <v-card-actions class="admin-dialog__actions">
          <v-spacer />
          <v-btn
            color="primary"
            class="mr-2"
            :disabled="!noteForm.note || isSaving"
            :loading="isSaving"
            @click="saveNote"
          >
            {{ editingNote ? 'Update' : 'Save' }}
          </v-btn>
          <v-btn outlined @click="cancelEdit">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import persistentDialogEsc from '@/mixins/persistentDialogEsc';

export default {
  name: 'GameNotes',
  components: {},
  mixins: [
    persistentDialogEsc({
      showAddNoteDialog: { close: 'cancelEdit', busy: 'isSaving' },
    }),
  ],
  props: {
    game: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      notes: [],
      search: '',
      isLoading: false,
      isSaving: false,
      showAddNoteDialog: false,
      editingNote: null,
      noteFormValid: false,
      noteForm: {
        note: '' // Only note field to match API
      },
      headers: [
        { text: 'Note', value: 'note', sortable: false },
        {
          text: 'Created',
          value: 'createdAt',
          width: '1%',
          class: 'game-notes__col--compact',
          cellClass: 'game-notes__col--compact',
        },
        {
          text: 'Updated',
          value: 'updatedAt',
          width: '1%',
          class: 'game-notes__col--compact',
          cellClass: 'game-notes__col--compact',
        },
        {
          text: 'Author',
          value: 'userName',
          width: '1%',
          class: 'game-notes__col--compact',
          cellClass: 'game-notes__col--compact',
        },
        {
          text: 'Actions',
          value: 'actions',
          sortable: false,
          align: 'center',
          width: '1%',
          class: 'game-notes__col--compact',
          cellClass: 'game-notes__col--compact',
        },
      ],
      rules: {
        required: value => !!value || 'Required.'
      },
      // Mock data for development/testing
      mockNotes: [
        {
          id: 1,
          title: 'Initial Setup',
          note: 'Game was initially set up with basic configuration. **Important**: Make sure to test all features.',
          createdAt: '2024-01-15T10:30:00Z',
          updatedAt: '2024-01-15T16:45:00Z',
          userName: 'john.doe@example.com'
        },
        {
          id: 2,
          title: 'Bug Fix',
          note: 'Fixed issue with *player movement* not working correctly in mobile devices.',
          createdAt: '2024-01-16T14:20:00Z',
          updatedAt: '2024-01-16T14:20:00Z', // Same as createdAt (never updated)
          userName: 'jane.smith@example.com'
        }
      ]
    };
  },
  watch: {
    'game.id': {
      immediate: true,
      handler(newId) {
        if (newId) {
          this.loadNotes();
        }
      }
    }
  },
  methods: {
    async loadNotes() {
      if (!this.game.id) return;
      
      this.isLoading = true;
      try {
        this.notes = await this.$gameApi.get(`/games/${this.game.id}/notes`);
      } catch (error) {
        console.error('Error loading notes:', error);
        // If it's a 404, the endpoint might not exist yet, so we'll show mock data for demo
        if (error.status === 404 || error.message?.includes('404')) {
          console.log('Notes endpoint not found, showing mock data for demo');
          this.notes = [...this.mockNotes];
        } else {
          this.$swal.fire({
            icon: 'error',
            title: 'Error loading notes',
            text: error.message || 'Failed to load notes',
            timer: 3000,
            showConfirmButton: false
          });
        }
      } finally {
        this.isLoading = false;
      }
    },
    
    noteChangeLogLink(note) {
      return {
        path: '/game-data-change-log',
        query: {
          table: 'game_note',
          entityId: String(note.id),
        },
      };
    },
    noteChangeLogHref(note) {
      return this.$router.resolve(this.noteChangeLogLink(note)).href;
    },
    editNote(note) {
      this.editingNote = note;
      // Normalize \n to empty string when loading data
      const noteValue = note.note || note.content || '';
      this.noteForm = {
        note: noteValue === '\n' ? '' : noteValue // Handle both API field names and normalize \n
      };
      this.showAddNoteDialog = true;
    },
    
    cancelEdit() {
      if (this.isSaving) return;
      this.showAddNoteDialog = false;
      this.editingNote = null;
      this.resetNoteForm();
    },
    
    resetNoteForm() {
      this.noteForm = {
        note: ''
      };
      this.$refs.noteForm?.resetValidation();
    },
    
    validateNoteForm() {
      if (this.$refs.noteForm) {
        return this.$refs.noteForm.validate();
      }
      
      if (!this.noteForm.note) {
        this.$swal.fire({
          icon: 'error',
          title: 'Validation Error',
          text: 'Please fill in all required fields',
          timer: 3000,
          showConfirmButton: false
        });
        return false;
      }
      
      return true;
    },
    
    showSuccessMessage(message) {
      this.$swal.fire({
        icon: 'success',
        title: message,
        timer: 1500,
        showConfirmButton: false
      });
    },
    
    showErrorMessage(title, message) {
      this.$swal.fire({
        icon: 'error',
        title,
        text: message,
        timer: 3000,
        showConfirmButton: false
      });
    },
    
    showWarningMessage(title, message) {
      this.$swal.fire({
        icon: 'warning',
        title,
        text: message,
        timer: 5000,
        showConfirmButton: false
      });
    },
    
    isApiNotFoundError(error) {
      return error.status === 404 || error.message?.includes('404');
    },
    
    async updateNoteApi() {
      await this.$gameApi.patch(`/games/${this.game.id}/notes/${this.editingNote.id}`, this.noteForm);
    },
    
    updateNoteMock() {
      const index = this.notes.findIndex(n => n.id === this.editingNote.id);
      if (index !== -1) {
        this.notes[index] = {
          ...this.notes[index],
          ...this.noteForm,
          updatedAt: new Date().toISOString()
        };
      }
    },
    
    async createNoteApi() {
      await this.$gameApi.post(`/games/${this.game.id}/notes`, this.noteForm);
    },
    
    createNoteMock() {
      const newNote = {
        id: Date.now(),
        note: this.noteForm.note,
        createdAt: new Date().toISOString(),
        userName: 'demo.user@example.com'
      };
      this.notes.unshift(newNote);
    },
    
    async handleNoteUpdate() {
      try {
        await this.updateNoteApi();
        this.showSuccessMessage('Note updated successfully');
      } catch (error) {
        if (this.isApiNotFoundError(error)) {
          this.updateNoteMock();
          this.showSuccessMessage('Note updated successfully (Demo Mode)');
        } else {
          throw error;
        }
      }
    },
    
    async handleNoteCreate() {
      try {
        await this.createNoteApi();
        this.showSuccessMessage('Note added successfully');
      } catch (error) {
        if (this.isApiNotFoundError(error)) {
          this.createNoteMock();
          this.showSuccessMessage('Note added successfully (Demo Mode)');
        } else {
          throw error;
        }
      }
    },
    
    handleSaveSuccess() {
      this.showAddNoteDialog = false;
      this.editingNote = null;
      this.resetNoteForm();
      this.loadNotes();
      this.$emit('update');
    },
    
    handleSaveError(error) {
      console.error('Error saving note:', error);
      
      if (this.isApiNotFoundError(error)) {
        this.showWarningMessage(
          'Notes API Not Available',
          'The notes API endpoint is not yet available. Please contact your administrator.'
        );
      } else {
        this.showErrorMessage('Error saving note', error.message || 'Failed to save note');
      }
    },
    
    normalizeNewlineToEmpty(value) {
      // If value is exactly '\n', replace with empty string
      return value === '\n' ? '' : value;
    },
    
    async saveNote() {
      console.log('Save note called', { noteForm: this.noteForm, gameId: this.game.id });
      
      if (!this.validateNoteForm()) {
        return;
      }
      
      // Normalize \n to empty string before saving
      if (this.noteForm.note === '\n') {
        this.noteForm.note = '';
      }
      
      this.isSaving = true;
      try {
        if (this.editingNote) {
          await this.handleNoteUpdate();
        } else {
          await this.handleNoteCreate();
        }
        
        await this.handleSaveSuccess();
      } catch (error) {
        this.handleSaveError(error);
      } finally {
        this.isSaving = false;
      }
    },
    
    async deleteNote(note) {
      const result = await this.$swal.fire({
        title: 'Delete Note?',
        text: `Are you sure you want to delete "${this.getNoteTitle(note)}"? This action cannot be undone.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel'
      });

      if (!result.isConfirmed) return;

      try {
        await this.$gameApi.delete(`/games/${this.game.id}/notes/${note.id}`);
        this.$swal.fire({
          icon: 'success',
          title: 'Note deleted successfully',
          timer: 1500,
          showConfirmButton: false
        });
        await this.loadNotes();
        this.$emit('update');
      } catch (error) {
        console.error('Error deleting note:', error);
        if (error.status === 404 || error.message?.includes('404')) {
          // Mock delete for demo
          this.notes = this.notes.filter(n => n.id !== note.id);
          this.$swal.fire({
            icon: 'success',
            title: 'Note deleted successfully (Demo Mode)',
            timer: 1500,
            showConfirmButton: false
          });
          this.$emit('update');
        } else {
          this.$swal.fire({
            icon: 'error',
            title: 'Error deleting note',
            text: error.message || 'Failed to delete note',
            timer: 3000,
            showConfirmButton: false
          });
        }
      }
    },
    
    getNoteTitle(note) {
      // If there's a title field, use it
      if (note.title) {
        return note.title;
      }
      
      // Otherwise, generate a title from the note content
      const content = note.note || note.content || '';
      if (!content) {
        return 'Untitled Note';
      }
      
      // Take the first line or first 50 characters, whichever is shorter
      const firstLine = content.split('\n')[0];
      const title = firstLine.length > 50 ? firstLine.substring(0, 50) + '...' : firstLine;
      
      // Remove HTML tags for cleaner display
      return title.replace(/<[^>]*>/g, '').trim() || 'Untitled Note';
    },
    
    formatDate(dateString) {
      if (!dateString) return 'Unknown';
      
      const date = new Date(dateString);
      const now = new Date();
      const diffInHours = Math.abs(now - date) / (1000 * 60 * 60);
      
      if (diffInHours < 24) {
        // Show relative time for recent dates
        if (diffInHours < 1) {
          const diffInMinutes = Math.round(diffInHours * 60);
          return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
        } else {
          const hours = Math.round(diffInHours);
          return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
        }
      } else {
        // Show full date for older dates
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    },
    
    formatNoteContent(content) {
      if (!content || typeof content !== 'string') {
        return '';
      }
      
      // First, escape any existing HTML to prevent XSS
      let safeContent = this.escapeHtml(content);
      
      // Then apply safe formatting
      safeContent = safeContent
        .replace(/\n/g, '<br>')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>');
      
      return safeContent;
    },
    
    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },
  }
};
</script>

<style scoped lang="scss" src="./GameNotes.scss"></style> 